import React, {useCallback, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {Linking, StyleSheet, View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {zodResolver} from '@hookform/resolvers/zod';
import {useFocusEffect} from '@react-navigation/native';

import Button from '../../components/themed/Button';
import Dialog from '../../components/ui-controls/Dialog';
import FormView from '../../components/ui-controls/form/FormView';
import TextInput from '../../components/ui-controls/form/TextInput';
import ValidationMessage from '../../components/ui-controls/form/ValidationMessage';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {
  ThirdPartyRequestData,
  ThirdPartyRequestSchema,
} from '../../models/schemas/thirdPartyRequestSchema.zod';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import apiService from '../../transport/apiService';
import {logger} from '../../utils/logger';

function InputLabel({children, required}: {children: React.ReactNode; required?: boolean}) {
  return (
    <Text style={styles.label}>
      {children}
      {!!required && (
        <Text style={styles.requiredAsterisk} accessibilityLabel="required field">
          {' *'}
        </Text>
      )}
    </Text>
  );
}

const SuccessDialog = ({isVisible, onOk, submittedTicketNumber}) => (
  <Dialog
    cancelVisible={false}
    isVisible={isVisible}
    okText="CLOSE"
    onOk={onOk}
    title="Your request was successfully submitted. Thank you.">
    <View>
      <Text style={styles.dialogText}>Your ticket number is #{submittedTicketNumber}</Text>
    </View>
  </Dialog>
);

const ErrorDialog = ({isVisible, onOk}) => (
  <Dialog
    cancelVisible={false}
    isVisible={isVisible}
    okText="CLOSE"
    onOk={onOk}
    title="Sorry, we couldn't submit your ticket at this moment. Please try again later."
  />
);

const ResponsibleServiceThirdPartyRequestScreen = ({
  navigation,
}: RouteNavigatorScreenProps<AppRoute.RESPONSIBLE_SERVICE_THIRD_PARTY_REQUEST>) => {
  const {userStore} = useStores();

  const [isSubmitting, setSubmitting] = useState(false);
  const [isSuccessDialogVisible, setSuccessDialogVisible] = useState(false);
  const [isErrorDialogVisible, setErrorDialogVisible] = useState(false);
  const [submittedTicketNumber, setSubmittedTicketNumber] = useState('');

  const defaultValues: ThirdPartyRequestData = {
    firstName: userStore.firstName || '',
    lastName: userStore.lastName || '',
    email: userStore.emailAddress || '',
    phone: userStore.phoneNumber || '',
    reviewPersonName: '',
    relationship: '',
    description: '',
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: {errors, touchedFields, isSubmitted},
  } = useForm<ThirdPartyRequestData>({
    resolver: zodResolver(ThirdPartyRequestSchema),
    defaultValues,
  });

  // Reset form on navigation blur (screen pattern - NOT useEffect)
  useFocusEffect(
    useCallback(() => {
      return () => {
        // Reset form on navigation blur
        reset();
      };
    }, [reset]),
  );

  const handleSuccessDialogOk = () => {
    setSuccessDialogVisible(false);
    navigation.navigate(AppRoute.HOME);
  };

  const handleErrorDialogOk = () => {
    setErrorDialogVisible(false);
  };

  const onSubmit = async (values: ThirdPartyRequestData) => {
    try {
      setSubmitting(true);
      const response = await apiService.createThirdPartyReviewRequest(values);
      const {ticketNumber} = response;
      if (!ticketNumber) {
        logger.error(
          'HelpContactThirdPartyRequestScreen',
          'Request was submitted successfully but no ticket number was received',
          {response},
        );
        throw new Error();
      }
      setSubmitting(false);
      setSubmittedTicketNumber(ticketNumber);
      setSuccessDialogVisible(true);
    } catch (_err) {
      setSubmitting(false);
      setErrorDialogVisible(true);
    }
  };

  const validationMessage = (field: keyof ThirdPartyRequestData) => {
    if (errors[field] && (touchedFields[field] || isSubmitted)) {
      return <ValidationMessage>{errors[field]?.message}</ValidationMessage>;
    }
    return null;
  };

  // The Android manifest is adjustPan, which never resizes the window, so the
  // keyboard inset has to be applied by the scroll view itself. DIS-1551.
  return (
    <KeyboardAwareScrollView
      style={{backgroundColor: 'white'}}
      keyboardShouldPersistTaps="always"
      enableOnAndroid
      extraScrollHeight={40}>
      <ContentView testID="third-party-request-screen">
        <Text style={styles.headline}>Request account review for someone else</Text>
        <Text style={styles.copy}>
          We are committed to the responsible service of alcohol and care about the wellbeing of our
          customers. If you are concerned that someone you know may have a problem with alcohol, you
          can contact us to review their account activity by filling in the below form. Your
          information will be handled in line with our privacy policy.{'\n'}
          {'\n'}You can also find information on support services{' '}
          <Text
            style={styles.textLink}
            onPress={() =>
              Linking.openURL(
                'https://drinkwiseaustr.wpengine.com/drinking-and-you/support-services/#',
              )
            }>
            here
          </Text>
          .
        </Text>
        <Text style={styles.requiredIndicator}>* indicates required field</Text>
        <FormView>
          <View>
            <View style={styles.fieldContainer}>
              <InputLabel required>First name</InputLabel>
              <Controller
                control={control}
                name="firstName"
                render={({field: {onChange, onBlur, value}}) => (
                  <TextInput
                    autoCapitalize="words"
                    autoComplete="name-given"
                    value={value}
                    onBlur={onBlur}
                    onChangeText={onChange}
                    placeholder="First name"
                    renderErrorMessage={false}
                    touched={touchedFields.firstName}
                  />
                )}
              />
              {validationMessage('firstName')}
            </View>
            <View style={styles.fieldContainer}>
              <InputLabel required>Last name</InputLabel>
              <Controller
                control={control}
                name="lastName"
                render={({field: {onChange, onBlur, value}}) => (
                  <TextInput
                    autoCapitalize="words"
                    autoComplete="name-family"
                    value={value}
                    onBlur={onBlur}
                    onChangeText={onChange}
                    placeholder="Last name"
                    renderErrorMessage={false}
                    touched={touchedFields.lastName}
                  />
                )}
              />
              {validationMessage('lastName')}
            </View>
            <View style={styles.fieldContainer}>
              <InputLabel required>Email</InputLabel>
              <Controller
                control={control}
                name="email"
                render={({field: {onChange, onBlur, value}}) => (
                  <TextInput
                    autoCapitalize="none"
                    autoComplete="email"
                    value={value}
                    keyboardType="email-address"
                    onBlur={onBlur}
                    onChangeText={onChange}
                    placeholder="Email"
                    renderErrorMessage={false}
                    touched={touchedFields.email}
                  />
                )}
              />
              {validationMessage('email')}
            </View>
            <View style={styles.fieldContainer}>
              <InputLabel required>Phone number</InputLabel>
              <Controller
                control={control}
                name="phone"
                render={({field: {onChange, onBlur, value}}) => (
                  <TextInput
                    autoCapitalize="none"
                    autoComplete="tel"
                    value={value}
                    keyboardType="number-pad"
                    onBlur={onBlur}
                    onChangeText={onChange}
                    placeholder="Phone number"
                    renderErrorMessage={false}
                    touched={touchedFields.phone}
                  />
                )}
              />
              {validationMessage('phone')}
            </View>
            <View style={styles.fieldContainer}>
              <InputLabel required>
                Name of the person who you would like to request a review
              </InputLabel>
              <Controller
                control={control}
                name="reviewPersonName"
                render={({field: {onChange, onBlur, value}}) => (
                  <TextInput
                    autoCapitalize="words"
                    autoComplete="off"
                    value={value}
                    onBlur={onBlur}
                    onChangeText={onChange}
                    placeholder="Name"
                    renderErrorMessage={false}
                    touched={touchedFields.reviewPersonName}
                  />
                )}
              />
              {validationMessage('reviewPersonName')}
            </View>
            <View style={styles.fieldContainer}>
              <InputLabel required>Your relationship with this person</InputLabel>
              <Controller
                control={control}
                name="relationship"
                render={({field: {onChange, onBlur, value}}) => (
                  <TextInput
                    autoCapitalize="words"
                    autoComplete="off"
                    value={value}
                    onBlur={onBlur}
                    onChangeText={onChange}
                    placeholder="Relationship"
                    renderErrorMessage={false}
                    touched={touchedFields.relationship}
                  />
                )}
              />
              {validationMessage('relationship')}
            </View>
            <View style={styles.fieldContainer}>
              <InputLabel required>Comments</InputLabel>
              <Controller
                control={control}
                name="description"
                render={({field: {onChange, onBlur, value}}) => (
                  <TextInput
                    numberOfLines={4}
                    autoCapitalize="words"
                    autoComplete="off"
                    value={value}
                    multiline={true}
                    onBlur={onBlur}
                    onChangeText={onChange}
                    renderErrorMessage={false}
                    touched={touchedFields.description}
                  />
                )}
              />
              {validationMessage('description')}
            </View>
          </View>
          <Button
            onPress={handleSubmit(onSubmit)}
            title="Submit request"
            disabled={isSubmitting}
            loading={isSubmitting}
          />
          <SuccessDialog
            isVisible={isSuccessDialogVisible}
            onOk={handleSuccessDialogOk}
            submittedTicketNumber={submittedTicketNumber}
          />
          <ErrorDialog isVisible={isErrorDialogVisible} onOk={handleErrorDialogOk} />
        </FormView>
      </ContentView>
    </KeyboardAwareScrollView>
  );
};

const styles = StyleSheet.create({
  dialogText: {
    fontSize: 16,
    marginBottom: 8,
    marginTop: 8,
  },
  textLink: {
    color: theme.colour.text.link.primary,
    fontSize: 16,
    fontWeight: 'bold',
  },
  headline: {
    fontSize: 20,
    color: theme.colour.text.heading.primary,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  copy: {
    fontSize: 16,
  },
  label: {
    fontSize: 16,
    marginBottom: 5,
  },
  requiredAsterisk: {
    fontSize: 16,
    color: '#ef4444',
    marginLeft: 2,
  },
  requiredIndicator: {
    fontSize: 13,
    color: '#6B7280',
    fontStyle: 'italic',
    marginTop: 8,
    marginBottom: 8,
  },
  fieldContainer: {
    marginBottom: 20,
  },
});

export default ResponsibleServiceThirdPartyRequestScreen;
